import {Box, Tabs, Tab} from "@mui/material";
import * as React from 'react';
import TabPanel from "../../../../share/components/tab/TabPanel";
import { ForestOwner } from "../../../types/models/ForestOwner";
import CuttingListItem from "../../../../timber/components/cutting/CuttingList/CuttingListItem";
import TimberStorageListItem from "../../../../timber/components/timberStorage/TimberStorageList/TimberStorageListItem";
import {useStateSession} from "../../../../hooks/useAuth";
import { t } from "@lingui/core/macro";
import GenericRootReport, {defaultFormOptions} from "../../generic/report/GenericRootReport.tsx";
import {toString} from "../../../types/models/Cutting.ts";
import PriceCalculator from "../../generic/priceCalculator/PriceCalculator.tsx";
import {formatDateTimeParam} from "../../../../utils/formatDate.ts";
import {endOfMonth, startOfMonth} from "date-fns";

type IFormProps = {
    data: ForestOwner;
};

const ForestOwnerTabs: React.FC<IFormProps> = ({ data = {} }) => {

    const session = useStateSession()
    const searchParams = new URLSearchParams(window.location.search)

    const [tabValue, setTabValue] = React.useState(searchParams.get('tab') || 'cutting');
    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        
        if (searchParams.has('tab')) {
            searchParams.set('tab', newValue)
        } else {
            searchParams.append('tab', newValue)
        }
        window.history.pushState({}, '', '?' + searchParams.toString())

        setTabValue(newValue);
    };

    const now = new Date();
    const customizeFiler = (filter: Record<string, string>): Record<string, string> =>  { return {
        ...filter,
        ownerId: data.id
    }}
    const reportTitle = `Forest Owner ${toString(data)}`
    const formOptions = {
        ...defaultFormOptions,
        showCutting: true,
    }

    return (
        <Box sx={ { width: '100%' } }>
            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                data-testid="ForestOwner-tabs"
            >
                { session.hasPermission('', 'applications/timber/entities/Cuttings/list/view') &&
                    <Tab label={t`Cuttings`} value="cutting"/>
                }
                { session.hasPermission('', 'applications/timber/entities/TimberStorages/list/view') &&
                    <Tab label={t`Timber Storages`} value="timberStorage"/>
                }
                { session.hasPermission('', 'applications/timber/entities/ForestOwners/report/view') &&
                    <Tab label={t`Report All`} value="reportAll"/>
                }
                { session.hasPermission('', 'applications/timber/entities/ForestOwners/report/view') &&
                    <Tab label={t`Report Not shipt`} value="reportNotShipt"/>
                }
                { session.hasPermission('', 'applications/timber/entities/ForestOwners/report/view') &&
                    <Tab label={t`Report Shipt`} value="reportShipt"/>
                }
                { session.hasPermission('', 'applications/timber/entities/PricingProfiles/view') &&
                    <Tab label={t`Price Calculator`} value="priceCalculatorReport"/>
                }
                </Tabs>
            
            { session.hasPermission('', 'applications/timber/entities/Cuttings/list/view') &&
                <TabPanel value={tabValue} index="cutting" data-testid="Cutting-tab">
                    <CuttingListItem forestOwnerId={data?.id} showAdd={ true } />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/TimberStorages/list/view') &&
                <TabPanel value={tabValue} index="timberStorage" data-testid="TimberStorage-tab">
                    <TimberStorageListItem forestOwnerId={data?.id} showAdd={ true } />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/ForestOwners/report/view') &&
                <TabPanel value={tabValue} index="reportAll">
                    <GenericRootReport
                        shipmentDefined={null}
                        mainTitle={reportTitle}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                        initialState={
                            {
                                startCreate: formatDateTimeParam(startOfMonth(now)),
                                endCreate: formatDateTimeParam(endOfMonth(now)),
                            }
                        }
                    />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/ForestOwners/report/view') &&
                <TabPanel value={tabValue} index="reportNotShipt">
                    <GenericRootReport
                        shipmentDefined={false}
                        mainTitle={reportTitle}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                        initialState={
                            {
                                startCreate: formatDateTimeParam(startOfMonth(now)),
                                endCreate: formatDateTimeParam(endOfMonth(now)),
                            }
                        }
                    />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/ForestOwners/report/view') &&
                <TabPanel value={tabValue} index="reportShipt">
                    <GenericRootReport
                        shipmentDefined={true}
                        mainTitle={reportTitle}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                        initialState={
                            {
                                startCreate: formatDateTimeParam(startOfMonth(now)),
                                endCreate: formatDateTimeParam(endOfMonth(now)),
                                startShipment: formatDateTimeParam(startOfMonth(now)),
                                endShipment: formatDateTimeParam(endOfMonth(now)),
                            }
                        }
                    />
                </TabPanel>
            }
            { session.hasPermission('', 'applications/timber/entities/PricingProfiles/view') &&
                <TabPanel value={tabValue} index="priceCalculatorReport">
                    <PriceCalculator
                        shipmentDefined={null}
                        customizeFiler={customizeFiler}
                        formOptions={formOptions}
                        initialState={
                            {
                                // Real dates, not formatted strings: the form
                                // is checked against a schema that wants dates,
                                // and the query is built from them.
                                startCreate: startOfMonth(now),
                                endCreate: endOfMonth(now),
                                startShipment: startOfMonth(now),
                                endShipment: endOfMonth(now),
                            }
                        }
                    />
                </TabPanel>
            }
            
        </Box>
    )
}

export default ForestOwnerTabs;
